import type { ActivityDefinition } from "@medplum/fhirtypes";
import { EXAM_INSTANCE_PROFILE } from "@repo/api";
import { asVisitId } from "@repo/domain";
import {
  CHARTING_OBSERVATION_KIND_SYSTEM,
  codeSystemUrl,
  extensionUrl,
  OBSERVATION_CATEGORY_SYSTEM,
} from "@repo/fhir-systems";
import { EXAM_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import type { ObservationRow } from "../fhir/types";
import { optimisticPatientReference } from "./_patient-reference";
import { optimisticVisitExecutionReference } from "./_visit-reference";

export function optimisticExam(
  input: { id: string; visitId: string; patientId: string; definitionUrl: string; expectedVersion: number },
  definitions: readonly ActivityDefinition[],
): ObservationRow {
  const definition = definitions.find(
    (row) => row.url === input.definitionUrl && row.version === String(input.expectedVersion),
  );
  if (!definition) throw new Error(`Exam definition '${input.definitionUrl}|${input.expectedVersion}' is not loaded`);
  return {
    resourceType: "Observation",
    identifier: [{ system: EXAM_ENTITY_ID_SYSTEM, value: input.id }],
    meta: { profile: [EXAM_INSTANCE_PROFILE], tag: [{ system: CHARTING_OBSERVATION_KIND_SYSTEM, code: "exam-panel" }] },
    status: "preliminary",
    code: { coding: [{ system: codeSystemUrl("exam-lineage"), code: input.definitionUrl }] },
    category: [{ coding: [{ system: OBSERVATION_CATEGORY_SYSTEM, code: "exam" }] }],
    subject: optimisticPatientReference(input.patientId),
    encounter: optimisticVisitExecutionReference(asVisitId(input.visitId)),
    extension: [
      {
        url: extensionUrl("exam-instantiates-canonical"),
        valueCanonical: `${input.definitionUrl}|${input.expectedVersion}`,
      },
    ],
    hasMember: [],
    domainId: input.id,
    patientId: input.patientId,
    visitId: input.visitId,
    examInstance: true,
    findingInstance: false,
    procedureJoinKey: `_noprocedure_${input.id}`,
    lifecycle: "Final",
    orderSetJoinKey: `_noorderset_${input.id}`,
    orderJoinKey: `_noorder_${input.id}`,
  };
}
export function optimisticProtocolExams(
  input: { id: string; patientId: string; examIds: Record<string, string> },
  examUrls: readonly string[],
  definitions: readonly ActivityDefinition[],
): ObservationRow[] {
  return [...new Set(examUrls)].map((definitionUrl) => {
    const definition = definitions
      .filter((row) => row.url === definitionUrl)
      .sort((a, b) => Number(b.version) - Number(a.version))[0];
    const id = input.examIds[definitionUrl];
    if (!definition?.version || !id) throw new Error(`Exam '${definitionUrl}' is not ready for booking`);
    return optimisticExam(
      { id, visitId: input.id, patientId: input.patientId, definitionUrl, expectedVersion: Number(definition.version) },
      definitions,
    );
  });
}
